import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import {
  ANIM,
  BRAND_DEPLOYMENT_GLOW,
  BRAND_DEPLOYMENT_GLOW_STOPS,
  CASINO_SETTINGS,
  COLORS,
  REDUCED_MOTION,
  STATUS_COLORS,
  STORYBOARD_CANVAS,
  STORYBOARD_COMPOSE,
} from '@/nova/theme/tokens';
import type { NodeStatus } from '../graph';

/* Compose choreography (defined above Card because the Card interpolates them). The storyboard is written IN
   the real card: while the model thinks, the beat cards show their true fields (Role / Motion / Duration),
   empty + disabled, and a soft accent glow BREATHES along the chain (staggered per beat, on each Card's
   `::before`) so the board reads as "working" with no shimmer. As each beat is reached its fields settle their
   values in (a one-shot ring), the motion prose types into its real textarea, then the card wakes to editable. */

/** Gentle enter for a beat card the first time it appears (Generate, or reopen): a short fade + rise. Applied
 *  via the Card's own `animation` so it plays once on mount (React Flow keys nodes by id -> no replay on
 *  re-derive) and never restarts when the seeding `::before` toggles. */
const cardEnter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Registers the animatable glow angle once (rendered a single time by StoryboardCanvas). On engines that
 *  ignore animating a registered property the arc simply sits static, the same fallback the deployment strip
 *  takes. A canvas-scoped name so it never collides with the deployment strip's own `--deployment-glow-angle`. */
export const CanvasGlowAngleProperty = createGlobalStyle`
  @property --sb-glow-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
`;

/** One loop of the Lumio glow travelling around a card edge. */
const glowSpin = keyframes`
  to {
    --sb-glow-angle: 360deg;
  }
`;

// The "working" travelling border (a card whose beat is being written OR rendered): the SAME masked-conic
// idiom the deployment strip's in-progress card uses (BounceCard.styles.ts) and the blog's generating row
// (GeneratingArticle.styles.ts) — one construction and stop list keyed off BRAND_DEPLOYMENT_GLOW(_STOPS), so
// "something is being built for you right now" reads identically wherever the studio says it. Change one and
// check the other two. A conic gradient whose `from <angle>` is an ANIMATED CSS custom property (a sweeping
// coloured arc), so it cannot be a static GRADIENTS token.
const GLOW_RING = `conic-gradient(from var(--sb-glow-angle), transparent 0 ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.62}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.36}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.09}%, transparent 100%)`; // design-allow
// Mask-composite alpha idiom that cuts the gradient down to a ring; the `#000` is a structural alpha source,
// not a colour.
const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

/** True while a card is being worked: its beat is being written by the model (compose, `$seedIndex` set) or
 *  its beat/frame is the one currently rendering in a run (`$status === 'active'`). Drives the travelling border. */
const isWorking = (p: { $status?: NodeStatus; $seedIndex?: number }): boolean =>
  p.$status === 'active' || p.$seedIndex != null;

/* Shared styling for the three canvas node types (frame / beat / output). Colours come only from theme
   tokens. Inputs live inside a NoDrag wrapper because the shared Select/TextArea/Field components take
   no className, and React Flow only leaves a node un-dragged when a `.nodrag` ancestor sits between the
   pointer target and the node element. */

/* Run-state chrome for a node card: the active node (being worked) shows the travelling Lumio border below
   (its run indicator), so its own border stays neutral, the studio accent (user-selectable red/green/blue)
   would otherwise show through the ring and clash. A completed node gets a quiet accent border; idle nodes
   (and the whole graph when nothing is running) stay neutral. Subtle by design. */
const statusChrome = (status: NodeStatus | undefined) => {
  if (status === 'done') {
    return css`
      border-color: ${COLORS.accent};
    `;
  }
  if (status === 'stale') {
    // Queued to be re-run by a regenerate/resume: a quiet dashed, desaturated caution-amber edge (no
    // glow) so it reads as "will change" without shouting. Subtle by design.
    return css`
      border-style: dashed;
      border-color: ${STORYBOARD_CANVAS.staleBorder};
    `;
  }
  return null;
};

export const Card = styled.div<{ $width: number; $status?: NodeStatus; $enter?: boolean; $seedIndex?: number }>`
  position: relative;
  width: ${(p) => p.$width}px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.panelBorder};
  box-sizing: border-box;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  ${(p) => statusChrome(p.$status)}
  ${(p) =>
    p.$enter &&
    css`
      animation: ${cardEnter} ${STORYBOARD_COMPOSE.CARD_ENTER_MS}ms ${ANIM.EASE} both;
    `}

  /* The "working" travelling Lumio border: a masked conic ring hugging the card's rounded edge (a soft GLOW,
     not a border). Always present but transparent, so it FADES in/out (opacity FADE_MS) when a beat starts or
     stops being worked, rather than popping. It only spins while working; during compose the per-beat delay
     staggers the sweep so it travels along the chain. Independent of the card's enter animation. */
  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    z-index: 1;
    border-radius: inherit;
    padding: 1.5px;
    background: ${GLOW_RING};
    -webkit-mask: ${RING_MASK};
    mask: ${RING_MASK};
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    filter: blur(2.5px);
    opacity: 0;
    transition: opacity ${BRAND_DEPLOYMENT_GLOW.FADE_MS}ms ease;
    pointer-events: none;
  }
  ${(p) =>
    isWorking(p) &&
    css`
      &::after {
        opacity: ${BRAND_DEPLOYMENT_GLOW.ALPHA};
        animation: ${glowSpin} ${BRAND_DEPLOYMENT_GLOW.SPIN_MS}ms linear infinite;
        ${p.$seedIndex != null &&
        css`
          animation-delay: -${(p.$seedIndex % 4) * 420}ms;
        `}
      }
    `}

  ${REDUCED_MOTION} {
    transition: none;
    animation: none;
    &::after {
      animation: none;
    }
  }
`;

/** Wrapper for a card's inputs. Nodes are not draggable (see StoryboardCanvas), so `.nodrag` is only a
 *  belt-and-suspenders guard here, it blocks neither panning nor zooming: you can still pan by dragging over
 *  the field and zoom by scrolling over it, while a deliberate click focuses/edits the field. */
export const NoDrag = styled.div.attrs({ className: 'nodrag' })`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Layout-transparent wrapper used to catch a field's bubbled focus/blur (React's onFocus/onBlur bubble)
// without changing the flex layout — `display: contents` removes its own box so the child lays out
// exactly as before. Lets a node know when an input is being edited so it won't re-seed it mid-type.
export const FocusScope = styled.div`
  display: contents;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

/** Run controls (Regenerate / View) shown on the right of a beat/frame header. A click fires the button; a
 *  drag or scroll over it still pans/zooms the board (nodes are not draggable). */
export const HeaderActions = styled.div.attrs({ className: 'nodrag' })`
  display: flex;
  gap: 6px;
`;

/** Small label pill (e.g. "Key visual", "Frame 1", role name). A static badge, not the toggle Chip. */
export const Pill = styled(Text).attrs({ tag: 'span' as const })`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceInset};
`;

/** Multi-promo: a small CTA-tinted chip naming the promotion a beat / key visual advertises, so the owner
 *  can see which promotion each beat belongs to. Only rendered when a storyboard has 2+ promotions. */
/** The finished-ad section of the output node: a divider above the version picker + the inline player. */
export const OutputVideo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const PromoChip = styled(Text).attrs({ tag: 'span' as const })`
  display: inline-flex;
  align-items: center;
  max-width: 150px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.02em;
  color: ${COLORS.ctaSolid};
  background: ${COLORS.ctaTint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Size a thumbnail to the video's aspect. Landscape fills the card width; portrait is height-capped and
// centred so a 9:16 card stays a sensible height instead of towering to ~2x the card width.
const thumbSizing = (ratio: number) =>
  ratio < 1
    ? css`
        height: 240px;
        width: auto;
        max-width: 100%;
        aspect-ratio: ${ratio};
        margin: 0 auto;
      `
    : css`
        width: 100%;
        aspect-ratio: ${ratio};
      `;

export const ThumbFrame = styled.div<{ $ratio: number; $dim?: boolean }>`
  position: relative;
  ${(p) => thumbSizing(p.$ratio)}
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  ${(p) =>
    p.$dim &&
    css`
      opacity: 0.45;
      filter: grayscale(0.4);
    `}
`;

export const ThumbImg = styled.img<{ $position?: string }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* The key visual is a promo banner whose subject usually sits to one side; F0 passes $position="right" so
     the subject stays in frame under the cover crop. Beat posters are generated frames (centred), so they
     keep the default. */
  object-position: ${(p) => p.$position ?? 'center'};
  display: block;
`;

export const ThumbPlaceholder = styled.div<{ $ratio: number }>`
  ${(p) => thumbSizing(p.$ratio)}
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${COLORS.textFaint};
  background: ${COLORS.surfaceInset};
  border: 1px dashed ${COLORS.hairline};
`;

export const PlaceholderText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

/** A round play badge centred over a beat's video-preview thumbnail (a pure-CSS triangle, no icon
 *  dependency). Brightens to accent on hover of its Thumb parent. Purely decorative (the Thumb button
 *  owns the click), so it ignores pointer events. */
export const PlayBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 46px;
  height: 46px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.overlaySoft};
  border: 1px solid ${COLORS.onAccent};
  pointer-events: none;
  transition:
    transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 7px 0 7px 12px;
    border-color: transparent transparent transparent ${COLORS.onAccent};
    margin-left: 3px;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** The beat card's clickable video-preview thumbnail: the beat's end frame under a play badge; a click
 *  opens the beat modal (the actual player), while a drag or scroll over it still pans/zooms the board. */
export const Thumb = styled.button.attrs({ className: 'nodrag', type: 'button' as const })<{
  $ratio: number;
  $dim?: boolean;
}>`
  position: relative;
  ${(p) => thumbSizing(p.$ratio)}
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  ${(p) =>
    p.$dim &&
    css`
      opacity: 0.45;
      filter: grayscale(0.4);
    `}

  &:hover ${PlayBadge} {
    background: ${COLORS.accent};
    transform: translate(-50%, -50%) scale(1.06);
  }
`;

/** A small caption on a stale node: its preview is kept but marked as needing a re-run. */
export const StaleNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  color: ${STORYBOARD_CANVAS.staleBorder};
`;

/** The failure reason pinned under the beat a failed run stopped on, so the owner sees WHERE it failed. */
export const ErrorNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  color: ${STATUS_COLORS.negative};
`;

/** The "no preview yet" body inside a Thumb before the beat has been generated. */
export const ThumbEmpty = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${COLORS.textFaint};
`;

/** Horizontal, scrollable strip of brand-image choices for a url end frame. */
export const BrandThumbs = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 2px;
`;

export const BrandThumb = styled.button<{ $selected: boolean }>`
  flex: 0 0 auto;
  width: 54px;
  height: 32px;
  padding: 0;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: ${COLORS.surfaceInset};
  border: 2px solid ${(p) => (p.$selected ? COLORS.accent : COLORS.hairline)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const EmptyBrandText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

export const Summary = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** The Brand card (a dedicated input node beneath the key visual): the logo + site domain baked into
 *  the finished ad, so the canvas makes the brand inputs explicit. */
export const BrandLogoBox = styled.div<{ $frame?: 'light' | 'dark' }>`
  width: 100%;
  aspect-ratio: 16 / 6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 10px;
  /* A light/dark plate chosen per-logo by luminosity (useAutoFrame), so a dark logo doesn't vanish on a
     dark surface — the same treatment used for every logo preview elsewhere in the app. */
  background: ${(p) =>
    p.$frame === 'dark'
      ? CASINO_SETTINGS.previewDark
      : p.$frame === 'light'
        ? CASINO_SETTINGS.previewLight
        : COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  box-sizing: border-box;
`;

export const BrandLogoLarge = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
`;

export const BrandDomainText = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  font-size: 12px;
  color: ${COLORS.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The move/remove controls in a beat's NodeToolbar. */
export const ToolbarRow = styled.div.attrs({ className: 'nodrag' })`
  display: flex;
  gap: 6px;
  padding: 4px;
  border-radius: 10px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.panelBorder};
`;

export const ToolButton = styled.button<{ $danger?: boolean; $flip?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? STATUS_COLORS.negative : COLORS.text)};
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};

  svg {
    transform: ${(p) => (p.$flip ? 'scaleX(-1)' : 'none')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/** One-shot highlight ring as a field's value lands during the reveal (`data-landed`). */
export const fieldSettleRing = keyframes`
  0% { box-shadow: 0 0 0 0 ${STORYBOARD_CANVAS.activeGlow}; }
  35% { box-shadow: 0 0 0 3px ${STORYBOARD_CANVAS.activeGlow}; }
  100% { box-shadow: 0 0 0 0 ${STORYBOARD_CANVAS.activeGlow}; }
`;

/** Wraps one field (Role / Motion / Duration) during compose so its value can "settle" in with a one-shot
 *  highlight ring the moment the beat starts typing (`data-landed`), staggered across the three fields by
 *  `$order`. Layout-transparent (`display: contents`) EXCEPT while landing, when it becomes a positioned box
 *  just long enough to paint the ring, so it never shifts the field. */
export const FieldSettle = styled.div<{ $order: number }>`
  display: contents;

  &[data-landed='true'] {
    display: block;
    border-radius: 10px;
    animation: ${fieldSettleRing} ${STORYBOARD_COMPOSE.FIELD_SETTLE_MS}ms ${ANIM.EASE}
      ${(p) => p.$order * STORYBOARD_COMPOSE.FIELD_STAGGER_MS}ms both;
  }

  ${REDUCED_MOTION} {
    &[data-landed='true'] {
      animation: none;
    }
  }
`;
